import { Flex, ScrollArea } from '@radix-ui/themes';
import { ErrorBoundary } from 'components/common/error-boundary';
import { STICKY_ACTIONS } from 'components/common/table/actions-cells';
import { CommonTableBody } from 'components/common/table/body';
import { TableContext, TableProvider } from 'components/common/table/context';
import { TableFooter } from 'components/common/table/footer';
import { TableListener } from 'components/common/table/listener';
import { TableToolbar } from 'components/common/table/toolbar';
import { CheckedProvider } from 'contexts/layout/checked.context';
import { ACTIONS_KEY, CHECKBOX_KEY } from 'enums/tables';
import useDeepCompareEffect from 'hooks/useDeepCompareEffect';
import { IVerboseComponent } from 'interfaces/i-components';
import { ITypedTableColumn } from 'interfaces/tables/columns';
import { ICommonTableProps } from 'interfaces/tables/main';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { useContext, useEffect, useMemo } from 'react';

type IProps = ICommonTableProps & IVerboseComponent;

// use this whenever the parent doesn't need to interact with the table context
export const CommonTableHoC = (props: IProps) => {
  return (
    <TableProvider
      identifier={props.identifier}
      verbose={props.verbose}
      skipResetSelected={props.skipResetSelected}
    >
      <CheckedProvider data={props.displayData}>
        <CommonTable {...props} />
      </CheckedProvider>
    </TableProvider>
  );
};

// use CommonTableHoC unless the parent component(s) need to interact with the contexts; otherwise wrap this in custom table and checked providers
export const CommonTable = (props: IProps) => {
  const {
    tableKey,
    initAfterChangeSelected,
    initBeforeSortFn,
    initColumns,
    initPaginationId,
    setData,
    setPageSize,
    setSort,
  } = useContext(TableContext);

  const {
    defaultSort,
    displayData,
    enablePagination,
    paginationID: paginationId,
    pageSizes,
    afterChangeSelected,
    beforeSortFn,
  } = props;

  const columns = useMemo<ITypedTableColumn<any>[]>(() => {
    const output = props.displayColumns.slice();

    if (props.checkboxColumnIndex !== undefined) {
      output.splice(props.checkboxColumnIndex, 0, {
        label: '#',
        key: CHECKBOX_KEY,
      });
    }

    const actionsIndex = output.findIndex((c) => c.key === ACTIONS_KEY);

    if (STICKY_ACTIONS && actionsIndex !== -1) {
      // move the actions column (wherever it is) to the end for sticky behaviour
      const actionsCol = output.splice(actionsIndex, 1)[0];
      output.push(actionsCol);
    }

    return output;
  }, [props.displayColumns, props.checkboxColumnIndex]);

  useEffect(() => {
    initColumns(columns);
  }, [columns]);

  useEffect(() => {
    if (paginationId) {
      initPaginationId(paginationId);
    }

    if (enablePagination && pageSizes && pageSizes.length > 0) {
      setPageSize(pageSizes[0], true);
    }
  }, [tableKey]);

  useDeepCompareEffect(() => {
    if (defaultSort) {
      setSort(defaultSort);
    }
  }, [defaultSort]);

  useEffect(() => {
    if (afterChangeSelected) {
      initAfterChangeSelected(afterChangeSelected);
    }
  }, [afterChangeSelected]);

  useEffect(() => {
    if (beforeSortFn) {
      initBeforeSortFn(beforeSortFn);
    }
  }, [beforeSortFn]);

  // apply changes to context whenever data changes (e.g. from filters above)
  useEffect(() => {
    setData(displayData);
  }, [displayData]);

  return (
    <ErrorBoundary identifier="CommonTable">
      <Flex
        data-identifier="CommonTable"
        direction="column"
        gap={RADIX.FLEX.GAP.SM}
        style={
          props.vFlex
            ? {
                height: '100%',
                width: '100%',
                position: 'relative',
                overflow: 'hidden',
              }
            : undefined
        }
      >
        <TableToolbar
          toolbarContent={props.toolbarContent}
          extraToolbarContent={props.extraToolbarContent}
        />

        {props.disableScrolling ? (
          <CommonTableBody {...props} columns={columns} />
        ) : (
          <ScrollArea data-identifier="CommonTableScrollBox" scrollbars="both">
            <CommonTableBody {...props} columns={columns} />
          </ScrollArea>
        )}

        {(props.enablePagination ||
          props.checkboxColumnIndex !== undefined) && (
          <TableFooter
            checkedActions={props.checkedActions}
            pageSizes={props.pageSizes}
            total={props.total}
            footerLeftSlot={props.footerLeftSlot}
            footerRightSlot={props.footerRightSlot}
          />
        )}
      </Flex>

      <TableListener
        enableListener={props.enableListener}
        onKeyActions={props.onKeyActions}
      />
    </ErrorBoundary>
  );
};
